import {BaseComponent} from "react-vextensions";
import {Row, RowLR, Column, Text, TextInput, Spinner, Button, CheckBox, TextArea} from "react-vcomponents";
import {AssertNotify, GetUpdates, Observer, RunInAction_Set, TextPlus, observer_simple} from "web-vcore";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {PropertiesUI_SharedProps} from "../PropertiesUI.js";
import React from "react";
import {GenericEntryInfoUI} from "../../../@Shared/CommonPropUIs/GenericEntryInfoUI.js";
import {GetObjAncestors, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {observer_mgl, RunInAction} from "mobx-graphlink";
import {UpdateMapObject} from "../../../../Server/Commands/MapObjectCommands.js";
import {Clone, CloneWithPrototypes} from "js-vextensions";
import {GetMapObject, GetMapObjects} from "../../../../Server/DB/mapObjects.js";
import {Vec3} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {CloneSafe} from "../../../../Utils/General/Serialization.js";
import {store} from "../../../../Store/index.js";
import {ShowMessageBox} from "react-vmessagebox";
import {DialogStyle} from "../../../../Utils/UI/GlobalStyles.js";
import {RectEditor} from "../../../../Utils/ReactComponents/RectEditor.js";
import {TransformWorldOffsetToObjLocalPoint_UsingRotationOnly} from "../../../../Utils/ThreeJS/SpaceUtils.js";

const splitAt = 100;

export const General_CompUI = observer_mgl((props: PropertiesUI_SharedProps)=>{
	const {map, mapState, obj, Change} = props;
	const uiState = store.main.maps.tools.propertyPanelStates.general;
	const parentPos_world = obj.parent != null ? ResolveObjPosInWorld(GetMapObject(obj.parent, map.id)) : null;
	const parentRot_world = obj.parent != null ? ResolveObjRotInWorld(GetMapObject(obj.parent, map.id)) : null;
	const objPos_world = ResolveObjPosInWorld(obj);
	const objRot_world = ResolveObjRotInWorld(obj);

	const mapObjects = GetMapObjects(map.id); // needed for random-placement button
	const childObjects = GetMapObjects(map.id, obj.id);
	//const childObjects_worldPositions = childObjects.ToMap(a=>a.id, a=>ResolveObjPosInWorld(a));
	//const childObjects_worldRotations = childObjects.ToMap(a=>a.id, a=>ResolveObjRotInWorld(a));

	const changeRot = (delta: number)=>{
		Change(newData=>{
			newData.rotation = ((newData.rotation ?? 0) + delta + 360) % 360;
		});
	};
	return (
		<Column>
			<Row>
				<GenericEntryInfoUI id={obj.id} creatorID={obj.creator} createdAt={obj.createdAt}/>
			</Row>
			<Row mt={5}>
				<Button text="JSON" onClick={()=>{
					let newJSON = JSON.stringify(obj, undefined, "\t");
					const controller = ShowMessageBox({
						title: "JSON of map-object (editable)", cancelButton: true,
						okOnEnterKey: false, // disable Enter hotkey, so user can add lines to text-area
						message: ()=><Column style={DialogStyle({width: 600})}>
							<TextArea editable={true} style={{width: "100%", height: 500}} value={newJSON} onChange={val=>{
								newJSON = val;
								controller.UpdateUI();
							}}/>
						</Column>,
						onOK: ()=>{
							const dataFromDialog = JSON.parse(newJSON);
							Change(newData=>{
								// clear all props on target map-object, then set new ones
								for (const key of Object.keys(newData)) {
									delete newData[key];
								}
								Object.assign(newData, dataFromDialog);
							});
						}
					});
				}}/>
				<Button ml={5} text="Place (randomly)" onClick={()=>{
					const controller = ShowMessageBox({
						title: "Place at random location", cancelButton: true,
						message: observer_simple(()=><Column style={DialogStyle({width: 600})}>
							<Row>
								<TextPlus info={`
									Don't set this region to be too far from [0,0], otherwise you'll get "jitter" issues. (due to floating-point precision limits on GPU)
									* Okay region (mesh with 1m guide-grid renders normally): {left: 0, right: 100_000, top: 200_000, bottom: 100_000}
									* Bad region (mesh with 1m guide-grid has jitter / messed-up-UVs): {left: 0, right: 1_000_000, top: 2_000_000, bottom: 1_000_000}
									See: https://discourse.threejs.org/t/large-coordinates/50621
								`.AsMultiline(0)}>Random placement region:</TextPlus>
							</Row>
							<RectEditor mt={5} ml={15} value={mapState.propertiesPanel.place_random_rect} onChange={val=>RunInAction_Set(()=>mapState.propertiesPanel.place_random_rect = val)}/>
							<Row mt={5}>
								<Text>Min dist from others:</Text>
								<Spinner ml={5} min={1} step={1} value={mapState.propertiesPanel.place_random_minDist} onChange={val=>RunInAction_Set(()=>mapState.propertiesPanel.place_random_minDist = val)}/>
								<Text>m</Text>
							</Row>
							<Row mt={5}>
								<Text>Attempts:</Text>
								<Spinner ml={5} min={1} step={1} value={mapState.propertiesPanel.place_random_attempts} onChange={val=>RunInAction_Set(()=>mapState.propertiesPanel.place_random_attempts = val)}/>
							</Row>
						</Column>),
						onOK: ()=>{
							const {place_random_rect, place_random_minDist, place_random_attempts} = mapState.propertiesPanel;

							// try to find valid position
							let newPos: Vec3|n;
							for (let i = 0; i < place_random_attempts; i++) {
								const randX = place_random_rect.x + Math.random() * place_random_rect.width;
								const randY = place_random_rect.y + Math.random() * place_random_rect.height;
								// if placement-rect is large enough (at least 100x100), round to whole numbers (cleaner)
								const pos_candidate = place_random_rect.width >= 100 && place_random_rect.height >= 100
									? new Vec3(Math.round(randX), Math.round(randY), obj.position.z)
									: new Vec3(randX, randY, obj.position.z);

								// check if any object is too close
								let tooClose = false;
								for (const otherObj of mapObjects) {
									if (otherObj.id == obj.id) continue;

									// maybe temp: if we can't synchronously resolve an object's ancestors or world-pos, notify user and just cancel whole thing
									// (should be niche to occur, and we don't want to pre-emptively call all this in General_CompUI's render just for this)
									const otherObjAncestors = GetObjAncestors.CatchBail(null, otherObj);
									AssertNotify(otherObjAncestors != null, `Could not resolve ancestors of object ${otherObj.id} when placing object ${obj.id} randomly.`);
									if (otherObjAncestors == null) return;
									const otherObjPos_world = ResolveObjPosInWorld.CatchBail(null, otherObj);
									AssertNotify(otherObjPos_world != null, `Could not resolve world-position of object ${otherObj.id} when placing object ${obj.id} randomly.`);
									if (otherObjPos_world == null) return;

									if (otherObjAncestors.Any(a=>a.id == obj.id)) continue; // ignore objects that are descendants of the object being randomly placed

									const dist = pos_candidate.DistanceTo(otherObjPos_world);
									if (dist < place_random_minDist) {
										tooClose = true;
										break;
									}
								}
								if (tooClose) continue;

								// found valid position
								newPos = pos_candidate;
								break;
							}

							if (newPos) {
								const newPos_local = newPos.Minus(parentPos_world ?? Vec3.zero);
								new UpdateMapObject({id: obj.id, updates: {position: newPos_local}}).RunOnServer();
							}
						}
					});
				}}/>
			</Row>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Parent:</Text>
				<TextInput style={{flex: 1}} value={obj.parent} onChange={val=>Change(a=>a.parent = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Order key:</Text>
				<TextInput style={{flex: 1}} value={obj.orderKey ?? ""} onChange={val=>Change(a=>a.orderKey = val || null)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`
					If name is "@", then auto-titling will be used. (eg. using title of linked dream; if label-override is set, that gets used)
					For names like "MyName <example>", cloning the object excludes the " <example>" part. (add-object w/prototype is also "cloning")
				`.AsMultiline(0)}>Name:</TextPlus>
				<TextInput style={{flex: 1}} value={obj.name} onChange={val=>Change(a=>a.name = val)}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<TextPlus info={`Tag "prototype": Enables this object as a "prototype" for use by the add-object tool.`}>Tags:</TextPlus>
				{obj.tags?.map((tag, index)=>(
					<TextInput key={index} mr={5} style={{width: 80}} value={tag} onChange={val=>{
						if (val.trim() == "") {
							Change(a=>a.tags!.RemoveAt(index));
						} else {
							Change(a=>a.tags![index] = val);
						}
					}}/>
				))}
				<Button mdIcon="plus" onClick={()=>{
					Change(a=>a.tags = (a.tags ?? []).concat("newTag"));
				}}/>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Location X:</Text>
				<Spinner step="any" min={Number.MIN_SAFE_INTEGER} style={{width: 120}} value={obj.position.x} onChange={val=>Change(a=>a.position.x = val)}/>
				<Text ml={5} sel>World X: {objPos_world.x}</Text>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Location Y:</Text>
				<Spinner step="any" min={Number.MIN_SAFE_INTEGER} style={{width: 120}} value={obj.position.y} onChange={val=>Change(a=>a.position.y = val)}/>
				<Text ml={5} sel>World Y: {objPos_world.y}</Text>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Location Z:</Text>
				<Spinner step="any" min={Number.MIN_SAFE_INTEGER} style={{width: 120}} value={obj.position.z} onChange={val=>Change(a=>a.position.z = val)}/>
				<Text ml={5} sel>World Z: {objPos_world.z}</Text>
			</RowLR>
			<RowLR mt={5} splitAt={splitAt}>
				<Text>Rotation:</Text>
				<Spinner step="any" style={{width: 120}} value={obj.rotation ?? 0} onChange={val=>Change(a=>a.rotation = val)}/>
				<Button ml={5} p="2px 5px" text="-15" onClick={()=>changeRot(-15)}/>
				<Button ml={5} p="2px 5px" text="+15" onClick={()=>changeRot(15)}/>
				<Button ml={5} p="2px 5px" text="-45" onClick={()=>changeRot(-45)}/>
				<Button ml={5} p="2px 5px" text="+45" onClick={()=>changeRot(45)}/>
				<Button ml={5} p="2px 5px" text="Fix" onClick={()=>changeRot(0)}/>
			</RowLR>
			<Row mt={5}>
				<Button text="Snap origin to cursor" onClick={()=>{
					ShowMessageBox({
						title: "Snap origin to cursor", cancelButton: true,
						message: `
							This will move the object so its origin is exactly at the 3d-cursor's position.
							WARNING: This function appears to have some bugs for objects/trees with rotation!
						`.AsMultiline(0),
						onOK: ()=>{
							const newPos_world = mapState.cursorPos.NewZ(objPos_world.z); // keep the object's Z-position the same (as cursor is 2d atm, for all practical purposes)
							//const newPos_local = newPos_world.Minus(parentPos_world ?? Vec3.zero);
							const posChange_world = newPos_world.Minus(objPos_world);
							const posChange_local = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(posChange_world.ToVec2(), parentRot_world ?? 0);
							const newPos_local = obj.position.Plus(posChange_local.ToVec3(0));

							const newObj = CloneSafe(obj);
							newObj.position = newPos_local;

							// apply offset correction to pinned meshes/paths
							if (uiState.snapOriginToCursor_pinMeshes) {
								for (const mesh of (newObj.meshes ?? [])) {
									mesh.points = mesh.points.map(a=>[a[0] - posChange_world.x, a[1] - posChange_world.y]);
								}
							}
							if (uiState.snapOriginToCursor_pinPaths) {
								for (const path of (newObj.paths ?? [])) {
									path.points = path.points.map(a=>[a[0] - posChange_world.x, a[1] - posChange_world.y]);
								}
							}
							// Check if any pinned mesh/path points would become misaligned with their grid after the offset.
							// Points not aligned to the grid cell size can corrupt grid-cell reconstruction (GridCellsToPolygon).
							const isOffGrid = (val: number, cellSize: number)=>Math.round(val / cellSize).Distance(val / cellSize) > 1e-9;
							const meshPointsWouldBeOffGrid = uiState.snapOriginToCursor_pinMeshes &&
								(newObj.meshes ?? []).some(mesh=>{
									const gs = mesh.guide_size;
									if (gs == null || gs == 0) return false;
									return mesh.points.some(([x, y])=>isOffGrid(x, gs) || isOffGrid(y, gs));
								});
							const pathPointsWouldBeOffGrid = uiState.snapOriginToCursor_pinPaths &&
								(newObj.paths ?? []).some(path=>{
									const gs = path.guide_size;
									if (gs == null || gs == 0) return false;
									return path.points.some(([x, y])=>isOffGrid(x, gs) || isOffGrid(y, gs));
								});

							const applySnap = ()=>{
								new UpdateMapObject({id: obj.id, updates: GetUpdates(obj, newObj)}).RunOnServer();
								//const posOverrideForObj_whileChangePending = {objId: obj.id, position_fieldOverride: newPos_local};
								if (uiState.snapOriginToCursor_pinChildren) {
									for (const child of childObjects) {
										/*const childPos_world_old = childObjects_worldPositions.get(child.id)!;
										const childPos_world_newButBeforeCorrection = ResolveObjPosInWorld(child, posOverrideForObj_whileChangePending);
										const worldOffsetRequiredToKeepChildAtSameWorldPos = childPos_world_old.Minus(childPos_world_newButBeforeCorrection!);*/
										const worldOffsetRequiredToKeepChildAtSameWorldPos = posChange_world.ToVec2().Times(-1); // invert, since if obj's moves X+10, child must move X-10 to keep its world-pos 
										const childLocalPosChangeToKeepChildWorldPos = TransformWorldOffsetToObjLocalPoint_UsingRotationOnly(worldOffsetRequiredToKeepChildAtSameWorldPos, objRot_world);
										const newChildPos_local = child.position.Plus(childLocalPosChangeToKeepChildWorldPos.ToVec3(0));
										new UpdateMapObject({id: child.id, updates: {position: newChildPos_local}}).RunOnServer();
									}
								}
							};

							if (meshPointsWouldBeOffGrid || pathPointsWouldBeOffGrid) {
								const affectedTypes = [meshPointsWouldBeOffGrid && "mesh", pathPointsWouldBeOffGrid && "path"].filter(Boolean).join(" and ");
								ShowMessageBox({
									title: "Warning: polygon points misaligned with grid",
									cancelButton: true,
									message: `
										This offset will result in coordinates for the pinned ${affectedTypes} point(s) that are not aligned with their grid cell size.
										Points misaligned with the grid can make grid-cell reconstruction imperfect. (eg. when using the mesh/path modify tool)
										Practical result: The polygon points will get "snapped" to nearest grid-cells, when next editing the mesh/path. (ie. polygon mutation)
										Proceed anyway?
									`.AsMultiline(0),
									onOK: applySnap,
								});
							} else {
								applySnap();
							}
						},
					});
				}}/>
				<Text ml={5}>Pin:</Text>
				<CheckBox ml={5} text="Meshes" value={uiState.snapOriginToCursor_pinMeshes} onChange={val=>RunInAction_Set(()=>uiState.snapOriginToCursor_pinMeshes = val)}/>
				<CheckBox ml={5} text="Paths" value={uiState.snapOriginToCursor_pinPaths} onChange={val=>RunInAction_Set(()=>uiState.snapOriginToCursor_pinPaths = val)}/>
				<CheckBox ml={5} text="Children" value={uiState.snapOriginToCursor_pinChildren} onChange={val=>RunInAction_Set(()=>uiState.snapOriginToCursor_pinChildren = val)}/>
			</Row>
			<Row mt={5}>
				<Button text="Snap cursor to object" onClick={()=>{
					RunInAction("General_CompUI.snapCursorToPos", ()=>mapState.cursorPos = objPos_world);
				}}/>
			</Row>
		</Column>
	);
});